import { useState } from 'react';
import { useRecoilState } from 'recoil';
import bgImage from '../assets/icons/appointment_crd.png';
import moment from 'moment/moment';
import myInfoAtom from '../atoms/myInfoAtom';
import { label, subTitle, title } from '../utils/style';

function EquipmentCard({ equipments = [] }) {
    const [myInfo] = useRecoilState(myInfoAtom);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(2);
    const [selectedStatus, setSelectedStatus] = useState('All');

    // Filter equipment based on selected status
    const filteredEquipments = selectedStatus === 'All' 
        ? equipments 
        : equipments.filter(equipment => equipment.status === selectedStatus);

    // Paginate filtered equipment
    const totalPages = Math.ceil(filteredEquipments.length / itemsPerPage);
    const paginatedEquipments = filteredEquipments.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="bg-customTwo rounded-xl p-4 text-center shadow-md">
            {/* Background Image */}
            <div>
                <img src={bgImage} alt="Equipment" className="w-full h-24 object-cover rounded-md" />
            </div>

            {/* Header */}
            {equipments.length === 0 ? (
                <div className={`${title} py-8 text-lg`}>No Equipment</div>
            ) : (
                <div>
                    <div className="py-3 flex justify-between items-center">
                        <div className={`${subTitle} text-base`}>{myInfo?.data?.firstName || 'User'}</div>
                        <div className={`${label} text-customOne text-sm cursor-pointer hover:underline`}>
                            Manage Equipment &gt;
                        </div>
                    </div>

                    {/* Filter and Equipment List Heading */}
                    <div className="mt-4 mb-2 flex justify-between items-center px-2">
                        <h3 className="font-semibold text-gray-600 text-sm">Equipment History</h3>
                        <div className="flex items-center gap-2">
                            <select
                                value={selectedStatus}
                                onChange={(e) => {
                                    setSelectedStatus(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="text-xs border rounded-md p-1 bg-white"
                            >
                                <option value="All">All</option>
                                <option value="Approved">Approved</option>
                                <option value="Pending">Pending</option>
                            </select>
                            <span className="text-xs text-gray-500">
                                {filteredEquipments.length} items
                            </span>
                        </div>
                    </div>

                    {/* Equipment List */}
                    {filteredEquipments.length === 0 ? (
                        <div className="py-4 text-sm text-gray-500">No equipment found matching the filter</div>
                    ) : (
                        <div className="space-y-2">
                            {paginatedEquipments.map((equipment, index) => (
                                <div key={index} className="bg-white p-3 rounded-lg shadow-sm">
                                    {/* Date and Status Row */}
                                    <div className="flex justify-between items-center mb-2">
                                        <div className="flex items-center space-x-1">
                                            <span className="text-xs font-medium text-gray-500">Date:</span>
                                            <span className="text-sm">
                                                {equipment.date}
                                            </span>
                                        </div>
                                        <span className={`px-2 py-1 rounded-full text-xs ${equipment.status === 'Approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {equipment.status || 'N/A'}
                                        </span>
                                    </div>

                                    {/* Check-In/Check-Out Row */}
                                    <div className="flex justify-between text-sm">
                                        <div className="flex items-center space-x-1">
                                            <span className="text-xs font-medium text-gray-500">Check-In:</span>
                                            <span>{equipment.checkinTime || 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <span className="text-xs font-medium text-gray-500">Check-Out:</span>
                                            <span>{equipment.checkoutTime || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination Controls */}
                    {filteredEquipments.length > itemsPerPage && (
                        <div className="mt-4 flex justify-between items-center px-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="text-xs bg-customOne text-white px-3 py-1 rounded-md disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="text-xs text-gray-500">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => (p < totalPages ? p + 1 : p))}
                                disabled={currentPage === totalPages}
                                className="text-xs bg-customOne text-white px-3 py-1 rounded-md disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default EquipmentCard;